import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SearchPicker } from '@/components/SearchPicker';
import { toast } from 'sonner';
import { Plus, Trash2, RefreshCw } from 'lucide-react';

const toRows = (meal) => (meal?.ingredients_list || []).map(i => ({ item: i.item, grams: i.grams ?? 0, food_id: i.food_id || null, state: i.state || 'raw' }));
const sig = (rs) => JSON.stringify(rs.map(r => [String(r.item).trim().toLowerCase(), Math.round(Number(r.grams) || 0)]));

/** Edit ONE day's planned meal (this client only). Macros come from the server's standard meal engine. */
export function EditPlanMeal({ open, onOpenChange, plan, idx, snap, foods, onSaved }) {
  const [name, setName] = useState('');
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [replacing, setReplacing] = useState(null);
  const [saving, setSaving] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!open) return;
    setName(snap?.meal?.name || ''); setRows(toRows(snap?.meal)); setReplacing(null);
    setPreview({ totals: { calories: snap?.meal?.calories, protein_g: snap?.meal?.protein_g, carbs_g: snap?.meal?.carbs_g, fat_g: snap?.meal?.fat_g }, ingredients: [] });
  }, [open]);

  // Unchanged ingredients keep the planned numbers; any change is re-priced live (debounced, latest wins).
  useEffect(() => {
    if (!open || rows.length === 0) return undefined;
    const m = snap?.meal || {};
    if (sig(rows) === sig(toRows(m))) {
      seq.current += 1;
      setPreview({ totals: { calories: m.calories, protein_g: m.protein_g, carbs_g: m.carbs_g, fat_g: m.fat_g }, ingredients: [] });
      return undefined;
    }
    const id = setTimeout(async () => {
      const n = ++seq.current;
      try {
        const r = await api.post('/meal-plans/edit-meal/preview', { ingredients: rows.filter(r => Number(r.grams) > 0).map(r => ({ ...r, grams: Number(r.grams) })) });
        if (n === seq.current) setPreview(r.data);
      } catch { /* keep previous numbers */ }
    }, 350);
    return () => clearTimeout(id);
  }, [rows, open]);

  const setRow = (i, patch) => setRows(rs => rs.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const addFood = (f) => f && setRows(rs => [...rs, { item: f.name, grams: f.serving_grams || 100, food_id: f.id || null, state: f.state || 'raw' }]);
  const save = async () => {
    if (!name.trim()) return toast.error('Give the meal a name');
    if (rows.length === 0) return toast.error('Add at least one ingredient');
    if (rows.some(r => !(Number(r.grams) > 0))) return toast.error('Every ingredient needs an amount above 0 g — or remove it');
    setSaving(true);
    try {
      const r = await api.post('/meal-plans/edit-meal', { assignment_id: plan.id, index: idx, name: name.trim(), ingredients: rows.map(r => ({ ...r, grams: Number(r.grams) || 0 })) });
      toast.success('Meal updated'); onSaved(r.data.meal); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save meal'); }
    finally { setSaving(false); }
  };
  const t = preview?.totals || {};
  const kcalOf = (i) => preview?.ingredients?.[i]?.macros?.calories;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-meal-dialog">
        <DialogHeader><DialogTitle>Edit {snap?.slot}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">Changes apply to this day's meal only. Your coach's recipe stays the same.</p>
        <Input value={name} onChange={e => setName(e.target.value)} data-testid="edit-meal-name" className="bg-[var(--c-film)] border-[var(--c-hair)] h-11 rounded-xl font-semibold" />
        <div className="grid grid-cols-4 gap-2 text-center font-mono" data-testid="edit-meal-totals">
          {[['kcal', t.calories, '#FFB800'], ['P', t.protein_g, 'var(--c-green-ink)'], ['C', t.carbs_g, '#00C8FF'], ['F', t.fat_g, '#7DD3FC']].map(([l, v, c]) => (
            <div key={l} className="regen-card p-2"><div className="text-[10px] text-muted-foreground">{l}</div><div className="text-base" style={{ color: c }} data-testid={`edit-meal-total-${l}`}>{Math.round(v || 0)}</div></div>
          ))}
        </div>
        <div className="space-y-2" data-testid="edit-meal-ingredients">
          {rows.map((r, i) => (
            <div key={i} className="regen-card p-2.5" data-testid={`edit-ing-${i}`}>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" data-testid={`edit-ing-name-${i}`}>{r.item}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{kcalOf(i) != null ? `${kcalOf(i)} kcal` : ' '}</div>
                </div>
                <div className="relative w-20">
                  <Input type="number" min="0" value={r.grams} onChange={e => setRow(i, { grams: e.target.value })} data-testid={`edit-ing-grams-${i}`} className="h-9 bg-[var(--c-film)] border-[var(--c-hair)] font-mono pr-5" />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">g</span>
                </div>
                <button onClick={() => setReplacing(replacing === i ? null : i)} data-testid={`edit-ing-replace-${i}`} title="Replace" className="w-8 h-8 rounded-lg hover:bg-[var(--c-film)] flex items-center justify-center text-[#00C8FF]"><RefreshCw className="w-3.5 h-3.5" /></button>
                <button onClick={() => setRows(rs => rs.filter((_, k) => k !== i))} data-testid={`edit-ing-remove-${i}`} title="Remove" className="w-8 h-8 rounded-lg hover:bg-[var(--c-film)] flex items-center justify-center text-[#FF5C5C]"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              {replacing === i && (
                <div className="mt-2">
                  <SearchPicker items={foods} value="" allowFreeText={false} clearOnPick placeholder={`Replace ${r.item} with…`} testid={`edit-ing-replace-search-${i}`}
                    onPick={(f) => { if (f) { setRow(i, { item: f.name, food_id: f.id || null, state: f.state || 'raw' }); setReplacing(null); } }} />
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="regen-card p-2.5">
          <div className="label-mini mb-1.5 flex items-center gap-1"><Plus className="w-3 h-3" /> Add ingredient</div>
          <SearchPicker items={foods} value="" allowFreeText={false} clearOnPick onPick={addFood} placeholder="Search food (Chicken, Rice, Spinach…)" testid="edit-meal-add-search" />
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-meal-cancel" className="border-[var(--c-hair)] h-11 rounded-xl">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-meal-save" className="h-11 rounded-xl bg-[var(--c-green)] hover:bg-[var(--c-green-soft)] text-[#081726] font-semibold">{saving ? 'Saving…' : 'Save Changes'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
